/**
 * Authentication service — handles login, logout, JWT tokens, and the
 * SSO broker handoff exchange.
 */
import { api } from './api.js';
import { store } from '../state/store.js';

// Central intranet — hosts the SSO broker for every app. Override at the
// app level by adding `<meta name="isg-intranet-url" content="...">` to
// index.html (e.g. for staging/dev hostnames). The default is the
// production intranet URL so unconfigured apps still work.
function _readIntranetUrl() {
  if (typeof document === 'undefined') return null;
  const meta = document.querySelector('meta[name="isg-intranet-url"]');
  const v = meta && meta.getAttribute('content');
  return v ? v.replace(/\/+$/, '') : null;
}
const SSO_BROKER_ORIGIN = _readIntranetUrl() || 'https://intranet.isgroup-international.com';

// When false, redirectToSSO() falls back to each app's legacy per-app Entra
// flow (/api/auth/entra/login on the current origin). Flip to true once the
// single `/auth/callback` URI has been added to the Entra app registration.
const SSO_BROKER_ENABLED = true;

// Non-httpOnly cookie set on the shared .isgroup-international.com domain to
// signal "this browser has an active ecosystem sign-in". Used only to decide
// whether to auto-redirect to the broker vs. show the login page. Contains
// no credentials — just a presence marker.
const SSO_HINT_COOKIE = 'isg_sso_hint';
const SSO_HINT_DOMAIN = '.isgroup-international.com';
// 7 days — refreshed on every login + every successful profile load so
// active users never lose it. The 24h window we ran with previously was
// shorter than typical user gaps and forced re-login on each app.
const SSO_HINT_MAX_AGE = 7 * 24 * 60 * 60;

function _setSSOHint() {
  if (typeof document === 'undefined') return;
  document.cookie =
    `${SSO_HINT_COOKIE}=1; domain=${SSO_HINT_DOMAIN}; path=/; `
    + `secure; samesite=lax; max-age=${SSO_HINT_MAX_AGE}`;
}

function _clearSSOHint() {
  if (typeof document === 'undefined') return;
  document.cookie =
    `${SSO_HINT_COOKIE}=; domain=${SSO_HINT_DOMAIN}; path=/; `
    + `secure; samesite=lax; max-age=0`;
}

function _hasSSOHint() {
  if (typeof document === 'undefined') return false;
  return document.cookie.split(';').some(c => c.trim().startsWith(`${SSO_HINT_COOKIE}=1`));
}

/**
 * Validate that a `next` param from the broker is a same-origin relative
 * path. Defence-in-depth — the broker already host-allowlists; this
 * stops any future bug there from becoming an open-redirect on the app.
 */
function _safeNextPath(next) {
  if (!next || typeof next !== 'string') return '/';
  // Must be a relative path starting with a single slash.
  if (!next.startsWith('/') || next.startsWith('//')) return '/';
  // Reject backslashes, schemes, control chars.
  if (/[\\\x00-\x1f]/.test(next)) return '/';
  return next;
}

class AuthService {
  async login(username, password) {
    const data = await api.post('/login', { username, password });
    // Cookies are set by the server (Phase B). The legacy localStorage
    // path stays populated during the transition for any client still
    // reading from it.
    if (data.access_token) api.token = data.access_token;
    if (data.refresh_token) api.refreshToken = data.refresh_token;
    _setSSOHint();
    await this.loadProfile();
    // Admin-created local accounts must set their own password before the
    // app shell renders — every app that calls auth.login() inherits this.
    if (data.must_change_password) {
      store.set('must_change_password', true);
    }
    return data;
  }

  /** Change the signed-in user's local password (clears the forced flag). */
  async changePassword(currentPassword, newPassword) {
    const res = await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    store.set('must_change_password', false);
    return res;
  }

  async register(username, password, email) {
    return await api.post('/register', { username, password, email });
  }

  /**
   * Boot-time auth-handoff capture. Apps call this synchronously at the
   * top of main.js, before any router or API code runs.
   *
   * Two cases:
   *   1. Phase B (current): URL is /auth/sso-callback?handoff=…&next=…
   *      → async POST to /api/auth/sso-exchange to swap the handoff JWT
   *        for httpOnly cookies, then navigate to ?next.
   *      → returns true; the page is navigating away, calling code
   *        should not continue rendering.
   *   2. Phase A back-compat: URL fragment carries access_token=…
   *      → stash into localStorage, strip fragment, return true.
   *
   * Returns false if neither applies (the common case — normal page load).
   */
  captureSSOHandoff() {
    if (typeof window === 'undefined') return false;

    // ── Phase B: callback page handoff ─────────────────────────────
    const path = window.location.pathname || '';
    if (path === '/auth/sso-callback' || path.endsWith('/auth/sso-callback')) {
      // Kick off the async exchange — it owns the navigation away from
      // here, so we just return true to signal "stop rendering".
      this.handleSSOCallbackPage();
      return true;
    }

    // ── Phase A: URL-fragment handoff (back-compat) ────────────────
    if (!window.location.hash) return false;
    const hash = window.location.hash.slice(1);
    if (!hash.includes('access_token=')) return false;

    const params = new URLSearchParams(hash);
    const token = params.get('access_token');
    if (!token) return false;

    api.token = token;
    const refresh = params.get('refresh_token');
    if (refresh) {
      api.refreshToken = refresh;
    }
    _setSSOHint();
    try {
      let pathname = window.location.pathname || '/';
      if (pathname === '/login' || pathname.endsWith('/login/')) {
        pathname = '/';
      }
      const clean = pathname + window.location.search;
      history.replaceState(null, '', clean);
    } catch (_) {
      /* older browsers — ignore */
    }
    return true;
  }

  /**
   * Phase B — exchange a broker-issued handoff JWT for httpOnly auth cookies.
   * Returns a Promise that resolves once the navigation is committed (the
   * caller should not continue to render the SPA — we're navigating away).
   *
   * Call as the very first thing in main.js so the user lands on
   * /auth/sso-callback?handoff=… and is sent home before anything else
   * tries to read auth state.
   */
  async handleSSOCallbackPage() {
    if (typeof window === 'undefined') return false;
    const path = window.location.pathname || '';
    if (path !== '/auth/sso-callback' && !path.endsWith('/auth/sso-callback')) {
      return false;
    }

    const params = new URLSearchParams(window.location.search);
    const handoff = params.get('handoff');
    const next = _safeNextPath(params.get('next'));

    if (!handoff) {
      window.location.replace('/login?sso_error=missing_handoff');
      return new Promise(() => {});
    }

    try {
      const data = await api.post('/auth/sso-exchange', { handoff });
      if (data && data.user) {
        store.set('user', data.user);
        store.set('isAuthenticated', true);
      }
      _setSSOHint();
      // Replace history so the back button doesn't return to the
      // single-use callback URL (which would 401 on retry).
      window.location.replace(next);
    } catch (err) {
      // Most common: handoff already used (double-tap, retry) or 30s
      // expiry. Surface a clean error and bounce to login — the user can
      // restart the broker round-trip from there.
      const reason = (err && err.data && err.data.error) || 'exchange_failed';
      window.location.replace(`/login?sso_error=${encodeURIComponent(reason)}`);
    }
    return new Promise(() => {});
  }

  /**
   * Start a sign-in flow. When SSO_BROKER_ENABLED is true, hands off to the
   * central intranet broker. Otherwise falls back to each app's legacy
   * per-app Entra endpoint so demos work before Entra has been reconfigured.
   */
  redirectToSSO(returnUrl) {
    if (SSO_BROKER_ENABLED) {
      const target = returnUrl || window.location.href;
      window.location.href =
        `${SSO_BROKER_ORIGIN}/auth/start?return=${encodeURIComponent(target)}`;
    } else {
      window.location.href = '/api/auth/entra/login';
    }
  }

  /**
   * Logout. When SSO_BROKER_ENABLED, routes through Entra end-session via
   * the broker; otherwise clears local state and goes to the app's login page.
   */
  async ssoLogout(returnUrl) {
    await this._serverLogout();
    api.token = null;
    api.refreshToken = null;
    store.set('user', null);
    store.set('isAuthenticated', false);
    _clearSSOHint();
    if (SSO_BROKER_ENABLED) {
      const back = returnUrl || `${SSO_BROKER_ORIGIN}/login`;
      window.location.href =
        `${SSO_BROKER_ORIGIN}/auth/logout?return=${encodeURIComponent(back)}`;
    } else {
      window.location.href = '/login';
    }
  }

  async _serverLogout() {
    // Cookie auth means we don't need a localStorage token to call logout —
    // the cookie is sent automatically. The body is kept to also revoke a
    // legacy localStorage refresh token if one is present.
    try {
      const body = api.refreshToken ? { refresh_token: api.refreshToken } : {};
      await api.post('/auth/logout', body);
    } catch (_) {
      // Network/server failure — proceed with local-only logout.
    }
  }

  async loadProfile() {
    try {
      const data = await api.get('/user/profile');
      const user = data.user || data;
      store.set('user', user);
      store.set('isAuthenticated', true);
      // Active session — keep the cross-app hint cookie alive so other
      // apps auto-redirect to the broker rather than showing /login.
      _setSSOHint();
      return user;
    } catch (err) {
      store.set('user', null);
      store.set('isAuthenticated', false);
      throw err;
    }
  }

  async checkSession() {
    // Don't race the SSO-callback exchange — captureSSOHandoff() owns
    // navigation away from this URL, but main.js's init() will keep
    // running and would otherwise punt the user to the broker before
    // the exchange completes.
    if (typeof window !== 'undefined') {
      const path = window.location.pathname || '';
      if (path === '/auth/sso-callback' || path.endsWith('/auth/sso-callback')) {
        return new Promise(() => {});
      }
    }

    if (!api.hasAuth()) {
      store.set('isAuthenticated', false);
      // Auto-hand-off to the broker only when *both*:
      //   (1) broker is enabled, AND
      //   (2) the shared-domain hint cookie says "this browser has an active
      //       ecosystem session elsewhere" — prevents cold visitors (bookmarks,
      //       direct URLs) from being punted to Microsoft unexpectedly.
      // Also skipped when the user deliberately navigated to /login (they
      // likely want local auth like dev_admin).
      if (SSO_BROKER_ENABLED && typeof window !== 'undefined' && _hasSSOHint()) {
        const path = window.location.pathname || '';
        const onLogin = path === '/login' || path.endsWith('/login/');
        if (!onLogin) {
          const returnUrl = window.location.href;
          window.location.href =
            `${SSO_BROKER_ORIGIN}/auth/start?return=${encodeURIComponent(returnUrl)}`;
          // Stop the calling code from continuing — the page is navigating away.
          return new Promise(() => {});
        }
      }
      return false;
    }

    try {
      await this.loadProfile();
      return true;
    } catch {
      api.token = null;
      store.set('isAuthenticated', false);
      return false;
    }
  }

  async logout() {
    await this._serverLogout();
    api.token = null;
    api.refreshToken = null;
    store.set('user', null);
    store.set('isAuthenticated', false);
    _clearSSOHint();
    const base = (typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL) || '/';
    window.location.href = base.replace(/\/$/, '') + '/login';
  }

  isAdmin() {
    const user = store.get('user');
    return user?.role === 'admin' || user?.is_admin === true;
  }

  isAppAdmin(appSlug) {
    if (this.isAdmin()) return true;
    if (!appSlug) return false;
    const user = store.get('user');
    const entry = user?.app_access?.[appSlug];
    return entry?.role === 'admin';
  }

  hasPermission(permission) {
    const user = store.get('user');
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.permissions?.includes(permission) || false;
  }
}

export const auth = new AuthService();
